import React, { MutableRefObject, useRef } from 'react';
import { Modal, Form, Button } from 'semantic';
import { useTranslation } from 'react-i18next';
import { BillingInfoFormikForm } from '../../../../BillingInfo/formik-form';
import { Formik, FormikProps } from 'formik';
import { useAccount } from 'contexts/account';
import {
  AccountBillingValues,
  useAccountBillingInitialState,
  useAccountBillingValidationSchema,
} from './formData';
import {
  AccountBillingErrorMessage,
  useAccountBillingErrorFormatter,
} from './errors';
import { useSubmitAccountBilling } from './api';
import { useFeatures } from 'contexts/features';
import { PaymentMethodsWidgetCallbacks } from 'components/PaymentMethodsWidget/PaymentMethodsWidget';
import {
  PaymentMethodsWidgetProvider,
  useEnabledPaymentMethods,
} from 'contexts/enabledPaymentMethods';

type Props = {
  onBack: () => void;
  stepHeader: string;
  billingFormRef: MutableRefObject<FormikProps<AccountBillingValues> | null>;
  paymentMethodWidgetRef: MutableRefObject<PaymentMethodsWidgetCallbacks | null>;
  /**
   * Triggered when a redirect is required (paybridge is enabled), I'm making the redirect triggered by the parent so
   * we have time to save any pending state before the redirect (loosing the state).
   **/
  onBillingDetailsSaved?: () => void;
  /**
   *
   * Triggered when the user clicks next but no redirect is required (paybridge is not enabled).
   **/
  onDone: () => void;
};
export default ({
  onDone,
  onBack,
  stepHeader,
  onBillingDetailsSaved,
  billingFormRef,
  paymentMethodWidgetRef,
}: Props) => {
  const { t } = useTranslation();
  const { getAccount } = useAccount();

  const { isPayBridgeEnabled } = useEnabledPaymentMethods();

  const { hasFeature, isReady: isFeatureReady } = useFeatures();
  const isBicCollectionEnabled = hasFeature('billing_bic_collection');

  const formInitialState = useAccountBillingInitialState();
  const formValidationSchema = useAccountBillingValidationSchema(
    isBicCollectionEnabled
  );
  const errorFormatter = useAccountBillingErrorFormatter();

  const submitAccountBillingRequest = useSubmitAccountBilling({
    onSuccess: async () => {
      if (isPayBridgeEnabled) {
        onBillingDetailsSaved?.();
        await getAccount?.();
        return;
      } else {
        onDone();
      }
    },
    onError: (error) => {
      error.innerError.details?.forEach((detail) =>
        errorFormatter(detail, billingFormRef.current)
      );
    },
  });

  const onSubmit = (values: AccountBillingValues) => {
    submitAccountBillingRequest.mutate(values);
  };

  const paymentAuthorizationRedirectLink = `${window.location.protocol}//${window.location.host}/my-locations`;

  return (
    <>
      <Modal.Header>
        {stepHeader}
        {t('newChargingStationBilling.header', 'Configure Account Billing')}
      </Modal.Header>
      <Modal.Content>
        <Formik
          innerRef={billingFormRef}
          initialValues={formInitialState.formData}
          onSubmit={onSubmit}
          validationSchema={formValidationSchema}>
          {({ handleSubmit }) => {
            return (
              <Form onSubmit={handleSubmit} id="account-billing-form">
                {submitAccountBillingRequest.isError && (
                  <AccountBillingErrorMessage
                    error={submitAccountBillingRequest.error}
                    onDismiss={() => submitAccountBillingRequest.reset()}
                  />
                )}
                {isFeatureReady && (
                  <PaymentMethodsWidgetProvider ref={paymentMethodWidgetRef}>
                    <BillingInfoFormikForm
                      accountType={formInitialState.accountType}
                      enableCountryCodeInput={
                        !formInitialState.formData?.billing?.countryCode
                      }
                      enableBicCollection={isBicCollectionEnabled}
                      objectPath="billing"
                      redirectUrl={paymentAuthorizationRedirectLink}
                    />
                  </PaymentMethodsWidgetProvider>
                )}
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
      <Modal.Actions>
        <Button content={t('common.back', 'Back')} onClick={() => onBack()} />

        <Button
          form="account-billing-form"
          primary
          content={t('common.next', 'Next')}
        />
      </Modal.Actions>
    </>
  );
};
